/**
 * The PIN that separates siblings on a shared tablet.
 *
 * Be honest about what this is: a courtesy lock, not an authorization boundary.
 * The guardian's session is the boundary. Four digits chosen by a nine-year-old
 * and watched over a shoulder by a seven-year-old protects nothing against an
 * attacker, and pretending otherwise would let it end up guarding something it
 * cannot guard. Nothing that moves money may depend on this check alone; RLS
 * and server-side issuance do that work.
 *
 * It is still hashed and still salted per child, because a four-digit number a
 * child also uses on a phone is not ours to keep in the clear.
 */

const encoder = new TextEncoder();

export const isValidPin = (pin) => /^\d{4}$/.test(String(pin ?? ''));

export function newPinSalt() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function hashPin(pin, salt) {
  if (!isValidPin(pin)) throw new Error('a PIN is four digits');
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(`${salt}:${pin}`));
  return `sha256:${salt}:${[...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
}

export async function verifyPin(pin, stored) {
  if (!stored) return true; // No PIN set is an open profile, not a locked one.
  const [, salt] = stored.split(':');
  if (!salt) return false;
  return (await hashPin(pin, salt)) === stored;
}
